import { toFixedWithoutRounding } from "@constants/common";
import { getGainApy } from "@services/index";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useIdle } from "react-use";
import { numberToCurrency } from "utils";
import { erc20Abi, formatUnits } from "viem";
import { mainnet } from "viem/chains";
import { useReadContract } from "wagmi";
import useDappConfig from "./useDappConfig";
import useStableAssetExchangeRate from "./useStableAssetExchangeRate";

const useStableVaultData = () => {
  const isIdle = useIdle(150e3);
  const { stableVaultConfig } = useDappConfig();
  const exchangeRateData = useStableAssetExchangeRate();

  const { data: apyData, isLoading: isLoadingApy } = useQuery({
    queryKey: ["gainApy"],
    queryFn: async () => {
      const { data } = await getGainApy();
      return data || {};
    },
    enabled: !isIdle,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const { data: contractTotalAssets, isLoading } = useReadContract({
    address: stableVaultConfig?.interactionPool.address,
    abi: stableVaultConfig?.interactionPool.abi,
    functionName: "totalAssets",
    chainId: mainnet.id,
    query: {
      enabled: !isIdle && !!stableVaultConfig,
      staleTime: 60_000,
      refetchInterval: 60_000,
    },
  });

  const { data: underlyingAsset } = useReadContract({
    address: stableVaultConfig?.interactionPool.address,
    abi: stableVaultConfig?.interactionPool.abi,
    functionName: "asset",
    chainId: mainnet.id,
    query: {
      enabled: !!stableVaultConfig,
    },
  });

  const { data: assetDecimals } = useReadContract({
    address: underlyingAsset as `0x${string}`,
    abi: erc20Abi,
    functionName: "decimals",
    chainId: mainnet.id,
    query: {
      enabled: !!underlyingAsset,
    },
  });

  const data = useMemo(() => {
    if (contractTotalAssets === undefined || contractTotalAssets === null) {
      return null;
    }

    try {
      const decimals = assetDecimals || 18;
      const normalized = formatUnits(contractTotalAssets as bigint, decimals);
      return {
        totalAssets: {
          normalized,
        },
      };
    } catch (err) {
      console.error("Error formatting contract data:", err);
      return null;
    }
  }, [contractTotalAssets, assetDecimals]);

  const totalTVL = useMemo(() => {
    if (
      data?.totalAssets?.normalized &&
      exchangeRateData.toERC20 &&
      !exchangeRateData.isLoading
    ) {
      const totalAssetsNum = Number(data.totalAssets.normalized);
      const exchangeRate = Number(exchangeRateData.toERC20);
      return totalAssetsNum * exchangeRate;
    }
    return null;
  }, [
    data?.totalAssets?.normalized,
    exchangeRateData.toERC20,
    exchangeRateData.isLoading,
  ]);

  const apy: number | null =
    apyData?.rsETH && !isNaN(+apyData?.rsETH) ? +apyData?.rsETH : null;

  return {
    isLoadingAPYData: isLoadingApy || isLoading,
    isLoadingTotalStaked: isLoading || exchangeRateData.isLoading,
    apy: apyData?.rsETH,
    totalStaked: totalTVL,
    tvl: totalTVL,
    ethStaked: data?.totalAssets?.normalized,
    formattedApy: apy ? `${toFixedWithoutRounding(apy.toString(), 2)}%` : "-",
    formattedTvl:
      totalTVL && !isNaN(Number(totalTVL))
        ? `${numberToCurrency(totalTVL.toFixed(2), 1)}`
        : "-",
    formattedETHStaked: "",
  };
};

export default useStableVaultData;
